<template>
  <v-sheet class="booking-date-time__container">
    <div class="sheet-title d-flex justify-space-between align-center py-4">
      <span class="wd-text-olg wd-font-medium md:wd-font-normal">{{ title }}</span>

      <v-btn
        v-if="crossday && bookingStartDate"
        class="reset__btn"
        height="32"
        color="orange50"
        __loading="$nuxt.$loading.show"
        variant="text"
        @click="resetBookingDate"
      >
        <span> {{ $t('reselect_time') }} </span>
      </v-btn>
      <div v-else-if="showTimeDiscountHint" class="hint-bar d-flex align-center">
        <div class="cycle-li mr-2" />
        <p class="ma-0">{{ $t('time_discount') }}</p>
      </div>
    </div>

    <div class="picker__content">
      <booking-seven-day-picker
        v-if="isSevenDays"
        :default-date="bookingStartDate"
        :best-off-peak="bestOffPeak"
        :arrival-times="arrivalTimes"
        @change="updateSevenBookingDate"
      />
      <booking-date-picker
        v-else
        :default-date="thirtyDayPickerDates"
        :best-off-peak="bestOffPeak"
        :is-cross-day="crossday"
        :arrival-times="arrivalTimes"
        :max-cross-days="maxCrossDays"
        @change="updateMonthBookingDate"
      />

      <v-divider />

      <div ref="timePicker" class="booking-time__swiper">
        <template v-if="isTimeSwiper">
          <!-- <template v-if="false"> -->
          <div>
            <!-- sm-time-swiper -->
            <time-swiper :symbol="symbol" :symbol-code="symbolCode" :rrp="rrp" :slide-group="slideGroup" @change="updateBookingTime" />
          </div>
        </template>
        <TimeBlockPicker v-else :selltimes="slideGroup" :default-sell-time="defaultStartDate" @change="updateBookingTime" />
      </div>
    </div>
  </v-sheet>
</template>

<script>
const BookingDatePicker = defineAsyncComponent(() => import('@/components/picker/booking-date-picker.vue'))
const BookingSevenDayPicker = defineAsyncComponent(() => import('@/components/picker/booking-seven-day-picker.vue'))
const TimeSwiper = defineAsyncComponent(() => import('@/components/swiper/time-swiper.vue'))
const TimeBlockPicker = defineAsyncComponent(() => import('@/components/picker/time-block-picker.vue'))

export default defineComponent({
  name: 'DateTimeModule',
  components: {
    BookingDatePicker,
    BookingSevenDayPicker,
    TimeSwiper,
    TimeBlockPicker,
  },
  props: {
    // Required
    arrivalTimes: { type: Object, default: () => ({}) },
    timeMode: { type: Number, default: 0 }, // { 0: 7天, 1: 30天 }
    checkoutTime: { type: String, default: '00:00:00' }, // if its value is empty string, still pass the props
    // Required(if it is TimeSwiper, and TimeSwiper is default)
    symbol: { type: String, default: '' },
    symbolCode: { type: String, default: '' },
    rrp: { type: Number, default: 1 },
    // Required(if it is cross-days)
    maxCrossDays: { type: Object, default: () => ({}) },
    // Optional
    startDay: { type: String, default: '' },
    defaultStartDate: { type: String, default: '' },
    bestOffPeak: { type: Object, default: () => ({}) },
    crossday: { type: Boolean, default: false },
    isTimeSwiper: { type: Boolean, default: true },
    showTimeDiscountHint: { type: Boolean, default: true },
    containerRef: { type: String, default: '' },
  },
  emits: ['triggerSideEffect', 'change'],
  setup() {
    const goTo = useGoTo()

    return {
      goTo,
    }
  },
  data() {
    return {
      monthDateArr: [],
      bookingStartDate: this.startDay || '',
      bookingStartTime: '',
      bookingEndDate: '',
      bookingDiscount: 0,
      bookingRemainCount: 0,
      timeoutId: 0,
    }
  },
  computed: {
    title() {
      if (this.crossday) {
        const key = this.bookingStartDate ? 'select_end_time' : 'select_start_time'
        return this.$t(key)
      }

      return this.$t('select_date')
    },
    isSevenDays() {
      return this.timeMode === 0
    },
    slideGroup() {
      if (this.arrivalTimes) {
        const bookingStartDate = this.bookingStartDate
        return this.arrivalTimes[bookingStartDate] || []
      }
      return []
    },
    bookingTimeMap() {
      return {
        startDate: this.bookingStartDate,
        startTime: this.bookingStartTime || this.arrivalTimes[this.bookingStartDate]?.[0]?.time || '',
        endDate: this.bookingEndDate,
        discount: this.bookingDiscount,
        remainCount: this.bookingRemainCount,
      }
    },
    thirtyDayPickerDates() {
      return this.crossday ? this.monthDateArr : this.bookingStartDate
    },
  },
  watch: {
    startDay(newDate) {
      if (!this.crossday) {
        this.bookingStartDate = newDate ?? ''
      }
      this.setEndDate(newDate)
    },
  },
  methods: {
    resetBookingDate() {
      console.log('will reset child data')
      this.monthDateArr = []
      this.bookingStartDate = ''
      this.bookingStartTime = ''
      this.bookingEndDate = ''
      this.bookingDiscount = 0
      this.bookingRemainCount = 0

      this.$emit('triggerSideEffect')
      this.emitChangPicker()
    },
    checkIsAfterCheckoutTime(startDay = new Date(), selectedTime) {
      const date = this.$dayjs(startDay).format('YYYY-MM-DD')
      const selectedDateTimeText = `${date} ${selectedTime}`
      const checkoutTimeText = `${date} ${this.checkoutTime}`
      return !this.$dayjs(selectedDateTimeText).isBefore(this.$dayjs(checkoutTimeText))
    },
    getNextDate() {
      const startDate = this.bookingStartDate
      const nextDate = startDate ? this.$dayjs(startDate).add(1, 'day').format('YYYY-MM-DD') : ''
      return nextDate
    },
    updateCrossDateRange(isAfterCheckoutTime) {
      const bookingDateArray = this.monthDateArr
      const nextDate = this.getNextDate()

      if (isAfterCheckoutTime && !bookingDateArray.includes(nextDate)) {
        bookingDateArray.splice(1, 0, nextDate)
        this.monthDateArr = bookingDateArray
      } else if (!isAfterCheckoutTime && bookingDateArray.includes(nextDate) && bookingDateArray.length === 2) {
        const index = bookingDateArray.indexOf(nextDate)
        bookingDateArray.splice(index, 1)
        this.monthDateArr = bookingDateArray
      }
    },
    setEndDate(selectedTime) {
      let endDate = this.bookingStartDate
      if (this.checkoutTime) {
        const isAfterCheckoutTime = this.checkIsAfterCheckoutTime(this.bookingStartDate, selectedTime)
        if (this.crossday) {
          this.updateCrossDateRange(isAfterCheckoutTime)
          endDate = this.monthDateArr.slice(-1)[0]
        } else if (isAfterCheckoutTime) {
          endDate = this.getNextDate()
        }
      }
      this.bookingEndDate = endDate
    },
    updateSevenBookingDate(bookingDate) {
      this.bookingStartDate = bookingDate
      this.setEndDate()
    },
    updateMonthBookingDate(bookingDateArray) {
      const goToOption = {
        duration: 1000,
      }
      if (this.containerRef) {
        goToOption.container = this.containerRef
      }
      this.goTo(this.$refs.timePicker, goToOption)
      // console.log(`bookingDateArray: `, bookingDateArray)
      const [startDate = '', ...restDates] = bookingDateArray
      const [endDate] = restDates.splice(-1)

      this.bookingStartDate = startDate

      if (endDate) {
        this.bookingEndDate = endDate
      } else {
        this.setEndDate()
      }
      this.monthDateArr = bookingDateArray
      this.emitChangPicker()
    },
    // TimeBlockPicker doesnt have `remain_count`
    updateBookingTime({ time, discount, remain_count = 0 }) {
      this.bookingStartTime = time
      this.bookingDiscount = discount
      this.bookingRemainCount = remain_count
      this.setEndDate(time)
      this.emitChangPicker()
    },
    emitChangPicker() {
      this.$emit('change', { ...this.bookingTimeMap, bookingDateArray: this.monthDateArr })
    },
  },
})
</script>

<style scoped lang="scss">
.booking-date-time__container {
  width: 100%;
  .sheet-title {
    width: 100%;

    .title-text {
      @include xxs {
        font-size: 11px;
        color: $gray80;
      }
    }

    .reset__btn {
      font-size: 15px;
    }

    .hint-bar {
      font-size: 13px;
      color: $gray80;
      .cycle-li {
        width: 4px;
        height: 4px;
        border-radius: 4px;
        background-color: $orange50;
      }

      @include xxs {
        font-size: 11px;
      }
    }
  }

  .picker__content {
    .booking-time__swiper {
      position: relative;
      font-size: 13px;
    }
  }
}
</style>
